// Floor sale, step 1: search or scan, pick a style.
import React, { useCallback, useMemo, useRef, useState } from "react";
import { formatMoney } from "../../../../utils/fashion/money";
import { colourLine, mrpFor, sizeRun } from "../../../../utils/fashion/browseFilters";
import { matchesStyle } from "./floorHelpers";
import { BagButton, FloorImage, Header } from "./FloorParts";
import FloorCameraScan, { cameraScanSupported } from "./FloorCameraScan";

const PAGE = 60;

// "S–XL" for a run of sizes, the size for one, "" for none.
const sizeRange = (sizes) => (sizes.length > 1 ? `${sizes[0]}–${sizes[sizes.length - 1]}` : sizes[0] || "");

// The tile's size strip, when stock is synced and the run is short enough to read.
const tileRun = (s) => {
  const run = sizeRun(s);
  return run.length >= 2 && run.length <= 8 && run.some((r) => r.state !== "unknown") ? run : null;
};

export default function FloorBrowse({ styles, categories, tillData, count, onBag, onOpenStyle, onScan, hasVariants }) {
  const [query, setQuery] = useState("");
  const [categoryId, setCategoryId] = useState("");
  const [limit, setLimit] = useState(PAGE);
  const [camera, setCamera] = useState(false);
  const canScan = useMemo(() => cameraScanSupported(), []);
  // The till re-renders often; a stable callback keeps the camera running.
  const onScanRef = useRef(onScan);
  onScanRef.current = onScan;
  const scanned = useCallback((code) => {
    setCamera(false);
    onScanRef.current(code);
  }, []);

  const chips = useMemo(() => {
    const used = new Set(styles.map((s) => s.categoryId).filter(Boolean));
    return (categories || [])
      .filter((c) => used.has(c.mProductCategoryId))
      .map((c) => ({ id: c.mProductCategoryId, name: c.name || c.value || "" }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [styles, categories]);

  const matching = useMemo(() => styles.filter((s) => (!categoryId || s.categoryId === categoryId) && matchesStyle(s, query)), [styles, categoryId, query]);

  // Enter (or Add) puts a tag's code on the bill through Core's scanner.
  const submit = (e) => {
    e.preventDefault();
    const code = query.trim();
    if (!code) return;
    onScan(code);
    setQuery("");
  };

  return (
    <>
      <Header>
        <b className="fp-fl-mk">Floor sale</b>
        <BagButton count={count} onClick={onBag} />
      </Header>
      <div className="fp-fl-body">
        <form className="fp-fl-srch" onSubmit={submit} role="search">
          <input
            className="fp-fl-field"
            aria-label="Search styles or enter a tag code"
            placeholder="Search style, or type a tag code"
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setLimit(PAGE);
            }}
            autoComplete="off"
            enterKeyHint="go"
          />
          <button type="submit" className="fp-fl-btn fp-fl-pri" disabled={!query.trim()}>
            Add tag
          </button>
          {canScan && (
            <button type="button" className="fp-fl-btn" onClick={() => setCamera(true)}>
              Scan
            </button>
          )}
        </form>
        {camera && <FloorCameraScan onCode={scanned} onClose={() => setCamera(false)} />}
        {chips.length > 1 && (
          <nav className="fp-fl-chips" aria-label="Categories">
            <button type="button" className={categoryId === "" ? "is-on" : ""} aria-pressed={categoryId === ""} onClick={() => setCategoryId("")}>
              All
            </button>
            {chips.map((c) => (
              <button key={c.id} type="button" className={categoryId === c.id ? "is-on" : ""} aria-pressed={categoryId === c.id} onClick={() => setCategoryId(c.id)}>
                {c.name}
              </button>
            ))}
          </nav>
        )}
        {!hasVariants && styles.length > 0 && <p className="fp-fl-hint">Sizes and colours aren't on this device yet. Run a Full Sync to load them.</p>}
        {matching.length === 0 ? (
          <div className="fp-fl-empty">
            <b>No styles found</b>
            <span>{query ? "Try a style name, or press Enter to add it as a tag code." : "Nothing in this category."}</span>
          </div>
        ) : (
          <div className="fp-fl-grid">
            {matching.slice(0, limit).map((s) => {
              const off = mrpFor(s);
              const run = tileRun(s);
              const facts = [colourLine(s), sizeRange(s.sizes)].filter(Boolean).join(" · ");
              return (
                <button key={s.styleId} type="button" className="fp-fl-tile" onClick={() => onOpenStyle(s)}>
                  <FloorImage src={s.imageUrl} alt={s.name} className="fp-fl-ph" art={s.art} colour={s.colours[0]} />
                  <span className="fp-fl-tn" title={s.name}>
                    {s.name}
                  </span>
                  {facts && <span className="fp-fl-tf">{facts}</span>}
                  <span className="fp-fl-tp fp-num">
                    {s.minPrice === s.maxPrice ? formatMoney(s.minPrice, tillData) : `${formatMoney(s.minPrice, tillData)}–${formatMoney(s.maxPrice, tillData)}`}
                    {off && <span className="fp-fl-off">−{off.offPct}%</span>}
                  </span>
                  {run && (
                    <span className="fp-fl-run" aria-label={`Sizes in this store: ${run.map((r) => `${r.size} ${r.state === "out" ? "none" : r.qty ?? "unknown"}`).join(", ")}`}>
                      {run.map((r) => (
                        <span key={r.size} className={`fp-fl-rs is-${r.state}`} aria-hidden="true" title={`${r.size}: ${r.qty ?? "?"}`} />
                      ))}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        )}
        {matching.length > limit && (
          <button type="button" className="fp-fl-btn" onClick={() => setLimit((n) => n + PAGE)}>
            Show more styles
          </button>
        )}
      </div>
    </>
  );
}
